/**
 * ThreadView — renders a focused thread inside the Feed app window.
 * Shows:
 *   - The parent chain (if any) above the focused note
 *   - The focused note itself, highlighted
 *   - Direct replies sorted by time
 */
import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import { useAuthor } from '@/hooks/useAuthor';
import { genUserName } from '@/lib/genUserName';
import { formatRelativeTime } from '@/lib/formatRelativeTime';
import { NoteContent } from '@/components/NoteContent';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { PostActions } from './PostActions';
import type { NostrEvent } from '@nostrify/nostrify';
import { ArrowLeft } from 'lucide-react';
import { useDesktop } from '@/desktop/DesktopContext';

// ─── Single note row (used for parent chain + replies) ────────────────────────

function NoteRow({
  event,
  focused = false,
  showLine = false,
}: {
  event: NostrEvent;
  focused?: boolean;
  showLine?: boolean;
}) {
  const author = useAuthor(event.pubkey);
  const meta = author.data?.metadata;
  const displayName = meta?.name ?? meta?.display_name ?? genUserName(event.pubkey);
  const { openProfile } = useDesktop();

  return (
    <div
      className={`px-3 py-3 ${
        focused
          ? 'bg-muted/30 border-l-2 border-primary'
          : 'border-b border-border/40 hover:bg-muted/10 transition-colors'
      }`}
    >
      <div className="flex gap-2.5">
        {/* Avatar + thread line */}
        <div className="flex flex-col items-center shrink-0">
          <button onClick={() => openProfile(event.pubkey)}>
            <Avatar className={focused ? 'w-10 h-10' : 'w-8 h-8'}>
              <AvatarImage src={meta?.picture} alt={displayName} />
              <AvatarFallback className="text-xs">{displayName.slice(0, 2).toUpperCase()}</AvatarFallback>
            </Avatar>
          </button>
          {showLine && (
            <div className="w-px flex-1 bg-border/50 mt-1.5 min-h-[20px]" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-1.5 mb-0.5 flex-wrap">
            <button
              className="font-semibold text-sm hover:underline truncate max-w-[160px]"
              onClick={() => openProfile(event.pubkey)}
            >
              {displayName}
            </button>
            {meta?.nip05 && (
              <span className="text-[10px] text-muted-foreground truncate max-w-[120px]">
                {meta.nip05.replace(/^_@/, '')}
              </span>
            )}
            <span className="text-[10px] text-muted-foreground ml-auto">
              {formatRelativeTime(event.created_at)}
            </span>
          </div>

          <NoteContent
            event={event}
            className={focused ? 'text-sm leading-relaxed' : 'text-sm leading-relaxed'}
            disableNoteEmbeds={false}
            disableMediaEmbeds={false}
          />

          <PostActions event={event} compact />
        </div>
      </div>
    </div>
  );
}

function ThreadSkeleton() {
  return (
    <div className="space-y-0">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="px-3 py-3 border-b border-border/40">
          <div className="flex gap-2.5">
            <Skeleton className="w-8 h-8 rounded-full shrink-0" />
            <div className="flex-1 space-y-1.5">
              <div className="flex gap-2">
                <Skeleton className="h-3.5 w-24" />
                <Skeleton className="h-3 w-14" />
              </div>
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-3/4" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Main ThreadView ──────────────────────────────────────────────────────────

interface ThreadViewProps {
  event: NostrEvent;
  onBack: () => void;
}

export function ThreadView({ event: initialEvent, onBack }: ThreadViewProps) {
  const { nostr } = useNostr();

  // If this is a shell event (created_at === 0), fetch the real event first
  const isShell = initialEvent.created_at === 0 && !initialEvent.content;
  const { data: fetchedEvent, isLoading: eventLoading } = useQuery({
    queryKey: ['event-by-id', initialEvent.id],
    queryFn: async ({ signal }) => {
      const results = await nostr.query(
        [{ ids: [initialEvent.id], limit: 1 }],
        { signal: AbortSignal.any([signal, AbortSignal.timeout(8000)]) }
      );
      return results[0] ?? null;
    },
    enabled: isShell,
    staleTime: 300000,
  });

  // Use fetched event when available, otherwise fall back to what was passed
  const event = (isShell ? fetchedEvent ?? initialEvent : initialEvent);

  // Fetch the parent chain (walk up via 'e' tags, max 3 levels)
  const rootId = event.tags.find(([t, , , m]) => t === 'e' && m === 'root')?.[1]
    ?? event.tags.filter(([t]) => t === 'e').at(-2)?.[1]; // second-to-last e is often root
  const replyId = event.tags.find(([t, , , m]) => t === 'e' && m === 'reply')?.[1]
    ?? event.tags.filter(([t]) => t === 'e').at(-1)?.[1]; // last e is the direct parent

  const parentIds = Array.from(new Set([rootId, replyId].filter((id): id is string =>
    !!id && id !== event.id
  )));

  const { data: parents, isLoading: parentsLoading } = useQuery({
    queryKey: ['thread-parents', event.id],
    queryFn: async ({ signal }) => {
      if (parentIds.length === 0) return [];
      const results = await nostr.query(
        [{ kinds: [1], ids: parentIds, limit: 10 }],
        { signal: AbortSignal.any([signal, AbortSignal.timeout(8000)]) }
      );
      // Sort by created_at ascending so oldest appears first
      return results.sort((a, b) => a.created_at - b.created_at);
    },
    staleTime: 60000,
  });

  // Fetch direct replies to this event
  const { data: replies, isLoading: repliesLoading } = useQuery({
    queryKey: ['thread-replies', event.id],
    queryFn: async ({ signal }) => {
      const results = await nostr.query(
        [{ kinds: [1], '#e': [event.id], limit: 100 }],
        { signal: AbortSignal.any([signal, AbortSignal.timeout(8000)]) }
      );
      // Filter to only direct replies (their last 'e' tag points to this event)
      return results
        .filter((e) => {
          const eTags = e.tags.filter(([t]) => t === 'e');
          return eTags.some(([, id]) => id === event.id);
        })
        .sort((a, b) => a.created_at - b.created_at);
    },
    staleTime: 30000,
  });

  const isLoading = eventLoading || parentsLoading || repliesLoading;

  return (
    <div className="flex flex-col h-full">
      {/* Title bar */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border/50 bg-muted/30 shrink-0">
        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={onBack}>
          <ArrowLeft className="w-3.5 h-3.5" />
        </Button>
        <span className="text-sm font-semibold">Thread</span>
      </div>

      <div className="flex-1 overflow-auto min-h-0">
        {isLoading ? (
          <ThreadSkeleton />
        ) : (
          <>
            {/* Parent chain */}
            {(parents ?? []).map((p) => (
              <NoteRow
                key={p.id}
                event={p}
                showLine /* every parent connects down to the focused note */
              />
            ))}

            {/* Focused note */}
            <NoteRow event={event} focused />

            {/* Divider */}
            {(replies ?? []).length > 0 && (
              <div className="px-3 py-2 border-b border-border/50 bg-muted/10">
                <span className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">
                  {replies?.length} {replies?.length === 1 ? 'Reply' : 'Replies'}
                </span>
              </div>
            )}

            {/* Replies */}
            {(replies ?? []).map((reply) => (
              <NoteRow key={reply.id} event={reply} />
            ))}

            {(replies ?? []).length === 0 && !repliesLoading && (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                No replies yet. Be the first to reply!
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
